"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConversationEventHandler = void 0;
const callback_resolver_helper_1 = require("./callback-resolver.helper");
const session_manager_helper_1 = require("./session-manager.helper");
/**
 * Static helper class for handling conversation agent WebSocket events
 */
class ConversationEventHandler {
    /**
     * Handle session started event
     */
    static handleSessionStarted(activeSessions, pendingCallbacks, wsResponse) {
        var _a;
        try {
            const data = wsResponse.data;
            const tempKey = data.tempKey;
            const sessionId = data.sessionId;
            const callbacks = session_manager_helper_1.SessionManager.activatePendingSession(activeSessions, pendingCallbacks, tempKey, sessionId);
            if (callbacks && sessionId) {
                (_a = callbacks.onSessionStarted) === null || _a === void 0 ? void 0 : _a.call(callbacks, sessionId, data.conversationId);
            }
        }
        catch (error) {
            console.error('Error handling session started:', error);
        }
    }
    /**
     * Handle session ended event
     */
    static handleSessionEnded(activeSessions, wsResponse) {
        var _a;
        try {
            const data = wsResponse.data;
            const sessionId = data.sessionId;
            const callbacks = activeSessions.get(sessionId);
            if (callbacks) {
                activeSessions.delete(sessionId);
                (_a = callbacks.onSessionEnded) === null || _a === void 0 ? void 0 : _a.call(callbacks, sessionId);
            }
        }
        catch (error) {
            console.error('Error handling session ended:', error);
        }
    }
    /**
     * Handle agent error event
     */
    static handleAgentError(activeSessions, pendingCallbacks, wsResponse, popupService, webChatComponent, endConversationCallback) {
        try {
            const data = wsResponse.data;
            const sessionId = data.sessionId;
            const tempKey = data.tempKey;
            const callbackExecuted = callback_resolver_helper_1.CallbackResolver.executeCallback(activeSessions, pendingCallbacks, sessionId, tempKey, 'onError', data.error || 'Unknown error occurred');
            if (!callbackExecuted) {
                popupService.error(data.error);
                console.log('chat', webChatComponent);
                if (webChatComponent) {
                    webChatComponent.emitEnd();
                }
                if (sessionId) {
                    console.log({ data, sessionId });
                    endConversationCallback(sessionId);
                }
            }
        }
        catch (error) {
            throw new Error(error || 'Unknown error occurred');
        }
    }
    /**
     * Handle user transcription event (both partial and final)
     */
    static handleUserTranscription(activeSessions, pendingCallbacks, wsResponse) {
        var _a, _b;
        try {
            const data = wsResponse.data;
            const { sessionId, transcription, isPartial, tempKey } = data;
            console.log('📝 [ConversationEventHandler] User transcription received:', {
                transcription,
                isPartial,
                sessionId
            });
            const callbacks = callback_resolver_helper_1.CallbackResolver.getCallbacksForSession(activeSessions, pendingCallbacks, sessionId, tempKey);
            if (callbacks) {
                if (isPartial) {
                    // Only call partial if we have actual content
                    if (transcription) {
                        (_a = callbacks.onPartialTranscription) === null || _a === void 0 ? void 0 : _a.call(callbacks, transcription);
                    }
                }
                else {
                    // Always call final transcription callback, even for empty - it might indicate STT failure
                    (_b = callbacks.onUserTranscription) === null || _b === void 0 ? void 0 : _b.call(callbacks, transcription || '');
                }
            }
        }
        catch (error) {
            console.error('Error handling user transcription:', error);
        }
    }
    /**
     * Handle partial transcription event
     */
    static handlePartialTranscription(activeSessions, pendingCallbacks, wsResponse) {
        try {
            const data = wsResponse.data;
            const { sessionId, transcription, tempKey } = data;
            console.log('📝 [ConversationEventHandler] Partial transcription:', { transcription, sessionId });
            callback_resolver_helper_1.CallbackResolver.executeCallback(activeSessions, pendingCallbacks, sessionId, tempKey, 'onPartialTranscription', transcription);
        }
        catch (error) {
            console.error('💥 [ConversationEventHandler] Error handling partial transcription:', error);
        }
    }
    /**
     * Handle audio response event
     */
    static handleAudioResponse(activeSessions, pendingCallbacks, wsResponse) {
        var _a;
        try {
            const data = wsResponse.data;
            console.log('🔊 [ConversationEventHandler] Audio response received:', {
                sessionId: data.sessionId,
                hasAudio: !!data.audio,
                responseLength: (_a = data.response) === null || _a === void 0 ? void 0 : _a.length
            });
            const callbacks = callback_resolver_helper_1.CallbackResolver.getCallbacksForSession(activeSessions, pendingCallbacks, data.sessionId, data.tempKey);
            if (callbacks) {
                // Call specific audio response callback if available
                if (callbacks.onAudioResponse && data.audio && data.response) {
                    callbacks.onAudioResponse(data);
                }
                // Also call general response callback for backward compatibility
                if (callbacks.onResponse) {
                    callbacks.onResponse({
                        ...data,
                        responseText: data.response,
                        responseAudio: data.audio
                    });
                }
            }
        }
        catch (error) {
            console.error('💥 [ConversationEventHandler] Error handling audio response:', error);
        }
    }
    /**
     * Handle text response event (TTS fallback)
     */
    static handleTextResponse(activeSessions, pendingCallbacks, wsResponse) {
        var _a;
        try {
            const data = wsResponse.data;
            console.log('📄 [ConversationEventHandler] Text response received (TTS fallback):', {
                sessionId: data.sessionId,
                responseLength: (_a = data.response) === null || _a === void 0 ? void 0 : _a.length,
                status: data.status
            });
            const callbacks = callback_resolver_helper_1.CallbackResolver.getCallbacksForSession(activeSessions, pendingCallbacks, data.sessionId, data.tempKey);
            if (callbacks) {
                // Call specific text response callback if available
                if (callbacks.onTextResponse && data.response) {
                    callbacks.onTextResponse(data);
                }
                // Also call general response callback for backward compatibility
                if (callbacks.onResponse) {
                    callbacks.onResponse({
                        ...data,
                        responseText: data.response,
                        status: data.status || 'listening'
                    });
                }
            }
        }
        catch (error) {
            console.error('💥 [ConversationEventHandler] Error handling text response:', error);
        }
    }
    /**
     * Handle stop recording event
     */
    static handleStopRecording(activeSessions, pendingCallbacks, wsResponse) {
        try {
            const data = wsResponse.data;
            console.log('🛑 [ConversationEventHandler] Stop recording event received:', {
                sessionId: data.sessionId
            });
            callback_resolver_helper_1.CallbackResolver.executeCallback(activeSessions, pendingCallbacks, data.sessionId, data.tempKey, 'onStopRecording', data);
        }
        catch (error) {
            console.error('💥 [ConversationEventHandler] Error handling stop recording:', error);
        }
    }
    /**
     * Handle agent status event
     */
    static handleAgentStatus(activeSessions, pendingCallbacks, wsResponse) {
        try {
            const data = wsResponse.data;
            callback_resolver_helper_1.CallbackResolver.executeCallback(activeSessions, pendingCallbacks, data.sessionId, data.tempKey, 'onStatusChange', data.status);
        }
        catch (error) {
            console.error('Error handling agent status:', error);
        }
    }
    /**
     * Handle conversation message event (for chat UI display)
     */
    static handleConversationMessage(activeSessions, pendingCallbacks, wsResponse) {
        try {
            const data = wsResponse.data;
            console.log('� Text message received for display');
            const callbacks = callback_resolver_helper_1.CallbackResolver.getCallbacksForSession(activeSessions, pendingCallbacks, data.sessionId, data.tempKey);
            if (callbacks) {
                // Call onConversationMessage callback if available (for adding to chat history)
                if (callbacks.onConversationMessage && data.role && data.content) {
                    callbacks.onConversationMessage({
                        role: data.role,
                        content: data.content,
                        timestamp: data.timestamp || new Date().toISOString()
                    });
                }
            }
        }
        catch (error) {
            console.error('💥 [ConversationEventHandler] Error handling conversation message:', error);
        }
    }
    /**
     * Handle generic events with minimal processing
     */
    static handleGenericEvent(eventName) {
        try {
            console.log(`[ConversationEventHandler] ${eventName} event received - no specific handling implemented`);
        }
        catch (error) {
            console.error(`Error handling ${eventName}:`, error);
        }
    }
}
exports.ConversationEventHandler = ConversationEventHandler;
